import { Box, Button, Container, Link, Stack, Typography } from '@mui/material';
import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isArray, isEmpty, join } from 'lodash';
import Stash, { CREATE_FAST_TRACK } from '../../utils/stash';
import { apiAxios } from '../../components';
import useInterval from '../../components/useInterval';
import { TopupIcon } from '../../components/topup_icon';
import { DEFAULT_FAST_TRACK_STASH_KEY } from './page';

interface PaymentStatusProps {
  referenceId: string;
}

const PaymentStatus: React.FC<PaymentStatusProps> = ({ referenceId }) => {
  const navigate = useNavigate();
  const popupWindowRef = useRef<Window | null>(null);
  const [popupOpened, setPopupOpened] = React.useState(false);
  const stash = Stash(CREATE_FAST_TRACK);
  const [paymentStatus, setPaymentStatus] = React.useState<'complete' | 'error' | 'pending'>('pending');
  const [jobStatus, setJobStatus] = React.useState<'complete' | 'error' | null>(null);
  const stashed = useMemo(() => stash.load(referenceId), [jobStatus, referenceId]);
  const referenceText =
    isArray(stashed?.jobRefs) && !isEmpty(stashed.jobRefs) ? join(stashed.jobRefs, ', ') : referenceId;

  const fetchChargedData = async () => {
    if (!referenceId) return;
    try {
      const response = await apiAxios({ method: 'get', url: `/charge_credit_card_699/${referenceId}` });
      const { data, success } = response.data;
      if (success) {
        setPaymentStatus(data.status);
      }
    } catch (error) {
      setPaymentStatus('error');
    }
  };

  useEffect(() => {
    if (referenceId) fetchChargedData();
  }, [referenceId]);

  useInterval(() => {
    if (paymentStatus !== 'pending') return;
    fetchChargedData();
  }, 5000); // 5000 ms = 5 seconds

  useEffect(() => {
    if (popupWindowRef.current && !popupWindowRef.current.closed) return;

    // If the stashed HTML is available, open the popup with it
    if (stashed?.otpFormHtml) {
      const popup = window.open('about:blank', '', '_blank');
      setPopupOpened(true);
      if (popup) {
        popupWindowRef.current = popup;
        popup.document.write(stashed.otpFormHtml);
        popup.document.close();
      }
    } else {
      popupWindowRef.current = null;
      setPopupOpened(false);
    }
  }, [stashed?.otpFormHtml]);

  // TODO: Fix auto close popup later
  // useInterval(() => {
  //   if (!popupOpened) return;

  //   if (popupWindowRef.current?.closed) {
  //     setPopupOpened(false);
  //     popupWindowRef.current = null;
  //     return;
  //   }

  //   try {
  //     const popupWindow = popupWindowRef.current;
  //     if (popupWindow && popupWindow.location) {
  //       const currentUrl = new URL(popupWindow.location.href);
  //       // If the URL has redirected to a specific path, close the popup
  //       if (includes(new URL(currentUrl, window.location.origin).pathname, `/fast_track/${referenceId}`)) {
  //         popupWindow.close();
  //         popupWindowRef.current = null;
  //         setPopupOpened(false);
  //       }
  //     }
  //   } catch (e) {
  //     setPopupOpened(false);
  //   }
  // }, 500); // 500 ms = 0.5 seconds

  useEffect(() => {
    if (paymentStatus === 'complete' && stashed.status !== 'complete') {
      stash.save(referenceId, { ...stashed, status: 'complete' });
      stash.remove(DEFAULT_FAST_TRACK_STASH_KEY);
      setJobStatus('complete');
    } else if (paymentStatus === 'error') {
      stash.save(referenceId, { ...stashed, status: 'error' });
      setJobStatus('error');
    }
  }, [paymentStatus, stashed.status]);

  const paymentStatusText = {
    complete: 'ชำระเงินสำเร็จแล้ว',
    error: 'เกิดข้อผิดพลาด',
    pending: 'กำลังตรวจสอบสถานะ',
  }[paymentStatus];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <TopupIcon topupStatus={paymentStatus} />
      <Container maxWidth="sm">
        <Stack alignItems="center" gap={1}>
          <Typography variant="h4">{paymentStatusText}</Typography>
          <Typography variant="body2">เมื่อประกาศจ้างถูกอนุมัติ เราจะแจ้งให้ทราบทางอีเมล</Typography>
          <Typography variant="body2">
            หากพบปัญหากรุณาติดต่อ{' '}
            <Link href="https://lin.ee/1ggzCzc" target="_blank">
              @findtemp
            </Link>
          </Typography>
          <Typography variant="caption" sx={{ mb: 2 }}>
            รหัสอ้างอิง: {referenceText}
          </Typography>
        </Stack>
      </Container>
      <Box sx={{ paddingRight: 3, paddingLeft: 3 }}>
        <Button variant="text" fullWidth color="primary" sx={{ mt: 1 }} onClick={() => navigate('/')}>
          กลับหน้าหลัก
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentStatus;
